/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint no-void: ["error", { "allowAsStatement": true }] */
import Button from 'components/Globals/Base/Button';
import { changeRoute } from 'components/Globals/Base/Link';
import Modal from 'components/Modal';
import NewsletterCard from 'components/NewsletterCard';
import NewsletterCarousel from 'components/NewsletterHeroCarousel';
import NewsletterSubscriptionTile from 'components/NewsletterSubscriptionTile';
import useBreakpoint from 'hooks/useBreakpoint';
import { Arrow } from 'icons/Arrow';
import { NewslettersLogo } from 'icons/NewslettersLogo';
import type { Newsletter } from 'interfaces/Account';
import Cookies from 'js-cookie';
import getUserNewsletterSubscriptions from 'lib/personalizationApi/getUserNewsletterSubscriptions';
import subscribeUser from 'lib/personalizationApi/subscribeUser';
import { useRouter } from 'next/router';
import { NoticeContext } from 'providers/NoticeProvider';
import { UserContext } from 'providers/UserProvider';
import { useContext, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import ErrorBoundary from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import { pushToDataLayer } from 'services/Gtm/functions';
import NEWSLETTERS_SECTION_TEST_IDS from 'constants/testsIds/newslettersPage';
import * as S from './NewslettersPageStyles';
import useNewslettersModal from './useNewslettersModal';
import showLogin from '@/main/lib/services/Piano/showLogin';

interface NewslettersPageProps {
  newsletters: Newsletter[];
  isGuest?: boolean;
}

const NewslettersPage = ({ newsletters, isGuest = false }: NewslettersPageProps) => {
  const isMobile = useBreakpoint('mdMax');
  const router = useRouter();
  const [loading, setLoading] = useState('initializing');
  const [userNewsletters, setUserNewsletters] = useState<Newsletter[]>(() =>
    [...newsletters].sort((news1, news2) => news1.category.localeCompare(news2.category)),
  );
  const {
    userState: { email, isLoggedIn, fetchError },
  } = useContext(UserContext);
  const { setNotification } = useContext(NoticeContext);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const { isModalOpen, modalSettings, setIsModalOpen, setModalTarget, setModalType } = useNewslettersModal(
    email,
    setUserNewsletters,
    setLoading,
    router.pathname,
  );

  const categorizedNewsletters: { [key: string]: Newsletter[] } = userNewsletters.reduce(
    (result, news) => ({
      ...result,
      [news?.category]: [...(result[news?.category] || []), news],
    }),
    {} as { [key: string]: Newsletter[] },
  );
  const tileNewsletters = userNewsletters.filter(
    (news) => news.subscriptionStatus && news.subscriptionStatus !== 'unsubscribed',
  );
  const carouselNewsletters = userNewsletters.filter((news) => news.showsInHero === 'true');
  const activeNewsletters = userNewsletters.filter(
    (newsletterer) => newsletterer.subscriptionStatus && newsletterer.subscriptionStatus === 'subscribed',
  );

  const handleSubscribeAll = async (formName: string[]) => {
    setLoading('updating');
    if (!executeRecaptcha) {
      setLoading('none');
      setNotification?.('error', 'There was an error fetching your account data. Please try again later.');
      console.error('executeRecaptcha is not defined.');
      return;
    }
    const recaptchaToken = executeRecaptcha && (await executeRecaptcha());
    if (!recaptchaToken) {
      console.error('reCAPTCHA verification failed.');
    }

    return subscribeUser(email, '', formName, recaptchaToken, router.pathname)
      .then(() => {
        setUserNewsletters((prevUserNews) => prevUserNews.map((n) => ({ ...n, subscriptionStatus: 'subscribed' })));
        setLoading('none');
        setIsModalOpen(true);
      })
      .catch(() => {
        setLoading('none');
        setNotification?.('error', 'Your subscription to all the newsletters could not be registered.');
      });
  };

  const handleSubscribe = async (news: Newsletter) => {
    setLoading('updating');
    if (!executeRecaptcha) {
      setLoading('none');
      console.error('executeRecaptcha is not defined.');
      setNotification?.('error', 'There was an error fetching your account data. Please try again later.');
      return;
    }
    const recaptchaToken = executeRecaptcha && (await executeRecaptcha());
    if (!recaptchaToken) {
      console.error('reCAPTCHA verification failed.');
    }

    return subscribeUser(email, '', [news.formName], recaptchaToken, router.pathname)
      .then(() => {
        setUserNewsletters((prevUserNews) =>
          prevUserNews.map((n) => {
            if (n.formName === news.formName) {
              return { ...n, subscriptionStatus: 'subscribed' };
            }
            return n;
          }),
        );
        setLoading('none');
        setIsModalOpen(true);
      })
      .catch(() => {
        setLoading('none');
        setNotification?.('error', `Your subscription to the ${news?.formName} could not be registered.`);
      });
  };

  const handleCookies = (formName: string) => {
    try {
      if (formName) {
        if (formName === 'all') {
          setModalType('subscribeToAll');
          const unsubscribedNewsletters = userNewsletters
            .filter((nl) => nl.subscriptionStatus !== 'subscribed')
            .map((newsletter) => newsletter?.formName);

          void handleSubscribeAll(unsubscribedNewsletters);
        } else {
          const subscribeNewsletter = newsletters.filter((newsletter) => newsletter.formName === formName)[0];

          setModalType('subscribe');
          setModalTarget([subscribeNewsletter]);
          void handleSubscribe(subscribeNewsletter);
        }
        Cookies.remove('newsletter_formName');
      }
    } catch (err) {
      console.error(err);
      setNotification?.(
        'error',
        'An unexpected error occurred while processing your newsletter subscription. Please try again later.',
      );
    }
  };

  const loggedInOnGuest = isLoggedIn && isGuest;
  const formName = Cookies.get('newsletter_formName') || '';

  useEffect(() => {
    if (loggedInOnGuest) {
      void changeRoute('/my-account/newsletters');
      setLoading('initializing');
    } else if (email && formName) {
      handleCookies(formName);
    } else if (fetchError) {
      setLoading('none');
      setNotification?.('error', 'There was an error fetching your account data. Please try again later.');
    }

    setLoading('none');
  }, [isLoggedIn, email, formName, fetchError]);

  useEffect(() => {
    if (isLoggedIn && email && !isGuest) {
      setLoading('updating');
      getUserNewsletterSubscriptions(email)
        .then((data) => {
          setUserNewsletters((prevList) =>
            prevList.map((news) => ({
              ...news,
              subscriptionStatus: data.includes(news.formName) ? 'subscribed' : 'unsubscribed',
            })),
          );
          setLoading('none');
        })
        .catch((error) => {
          setLoading('none');
          if (fetchError) {
            setNotification?.(
              'error',
              'There was an error fetching your newsletter subscription data. Please try again later.',
            );
            console.error('Error: ', error);
          } else {
            setNotification?.('warning', 'Your account is currently being created, your data will update momentarily.');
          }
        });
    }
  }, [isLoggedIn, email, setNotification, fetchError]);

  const getLoadingState = () => {
    switch (loading) {
      case 'initializing':
        return 'isLoading';
      case 'updating':
        return `overlayLoad ${isGuest ? 'guest' : ''}`;
      default:
        return 'none';
    }
  };

  return (
    <S.Wrapper
      data-cy={NEWSLETTERS_SECTION_TEST_IDS.NEWSLETTERS_WRAPPER}
      className={isGuest ? 'guest' : ''}
      id='newsletters'
    >
      <ErrorBoundary fragment={UIFragments.NEWSLETTER_PAGE}>
        <Modal
          data-cy={NEWSLETTERS_SECTION_TEST_IDS.NEWSLETTERS_MODAL}
          className={modalSettings.className}
          isOpened={isModalOpen}
          title={modalSettings.title}
          onClose={modalSettings.onClose}
          firstButton={
            modalSettings.firstButton
              ? {
                  onClick: modalSettings.firstButton.onClick,
                  title: modalSettings.firstButton.title,
                  variant: modalSettings.firstButton.variant,
                }
              : undefined
          }
          secondButton={
            modalSettings.secondButton
              ? {
                  onClick: modalSettings.secondButton.onClick,
                  title: modalSettings.secondButton.title,
                  variant: modalSettings.secondButton.variant,
                }
              : undefined
          }
        >
          {modalSettings.children}
        </Modal>
      </ErrorBoundary>
      <ErrorBoundary fragment={UIFragments.NEWSLETTER_PAGE}>
        <S.StickyHeader data-cy={NEWSLETTERS_SECTION_TEST_IDS.NEWSLETTERS_STICKY}>
          {isMobile && (
            <Button
              data-cy={NEWSLETTERS_SECTION_TEST_IDS.NEWSLETTERS_BACK_BUTTON}
              className='back-button'
              onClick={() => {
                void changeRoute('/my-account/');
              }}
              variant='tertiaryBlack'
              hasRightArrow={false}
            >
              <Arrow /> Back
            </Button>
          )}
          <NewslettersLogo data-cy={NEWSLETTERS_SECTION_TEST_IDS.NEWSLETTERS_LOGO} />
        </S.StickyHeader>
      </ErrorBoundary>

      <div className={getLoadingState()}>
        {!isGuest && (
          <ErrorBoundary fragment={UIFragments.NEWSLETTER_PAGE}>
            <S.SubscriptionsWrapper
              id='subscriptions'
              data-cy={NEWSLETTERS_SECTION_TEST_IDS.SUBSCRIPTIONS_WRAPPER}
            >
              <S.TitleWrapper>
                <S.SubscriptionsTitle data-cy={NEWSLETTERS_SECTION_TEST_IDS.SUBSCRIPTIONS_TITLE}>
                  My Subscriptions
                </S.SubscriptionsTitle>
                {activeNewsletters.length ? (
                  <button
                    data-cy={NEWSLETTERS_SECTION_TEST_IDS.UNSUBSCRIBE_ALL}
                    type='button'
                    className='unsub-all-btn'
                    onClick={() => {
                      setModalType('unsubscribeFromAll');
                      setModalTarget(tileNewsletters);
                      setIsModalOpen(true);
                    }}
                  >
                    Unsubscribe from all
                  </button>
                ) : null}
              </S.TitleWrapper>
              {tileNewsletters.length === 0 && loading === 'none' && (
                <S.SubscriptionPlea data-cy={NEWSLETTERS_SECTION_TEST_IDS.SUBSCRIPTIONS_PLEA}>
                  You are not subscribed to any newsletters. You can subscribe{' '}
                  <a
                    data-cy={NEWSLETTERS_SECTION_TEST_IDS.SUBSCRIPTIONS_PLEA_LINK}
                    href='#newsletters'
                  >
                    here
                  </a>
                  .
                </S.SubscriptionPlea>
              )}
              <S.SubscriptionsList data-cy={NEWSLETTERS_SECTION_TEST_IDS.SUBSCRIPTIONS_LIST}>
                {tileNewsletters.map((news) => (
                  <NewsletterSubscriptionTile
                    data-cy={NEWSLETTERS_SECTION_TEST_IDS.NEWSLETTER_SUBSCRIPTIONS_TITLE}
                    key={news.title}
                    title={news.title}
                    subscriptionStatus={news.subscriptionStatus}
                    onClick={() => {
                      setModalType(news.subscriptionStatus === 'subscribed' ? 'unsubscribe' : 'subscribe');
                      setModalTarget([news]);
                      if (news.subscriptionStatus !== 'subscribed') {
                        void handleSubscribe(news);
                      } else {
                        setIsModalOpen(true);
                      }
                    }}
                  />
                ))}
              </S.SubscriptionsList>
            </S.SubscriptionsWrapper>
          </ErrorBoundary>
        )}
        <ErrorBoundary fragment={UIFragments.NEWSLETTER_PAGE}>
          <S.CarouselHeroWrapper data-cy={NEWSLETTERS_SECTION_TEST_IDS.CAROUSEL_HERO_WRAPPER}>
            {activeNewsletters.length !== newsletters.length && (
              <button
                data-cy={NEWSLETTERS_SECTION_TEST_IDS.SUBSCRIBE_TO_ALL}
                type='button'
                className='sub-all-btn'
                onClick={() => {
                  const unsubscribedNewsletters = userNewsletters.filter(
                    (nl) => nl.subscriptionStatus !== 'subscribed',
                  );
                  const newsletterTitles = unsubscribedNewsletters
                    .map((newsletter) => `${newsletter.title} Newsletter`)
                    .join(',');
                  pushToDataLayer({
                    event: 'formInteract',
                    formName: newsletterTitles,
                  });
                  if (email) {
                    setModalType('subscribeToAll');
                    void handleSubscribeAll(unsubscribedNewsletters.map((newsletter) => newsletter?.formName));
                  } else {
                    showLogin();
                    Cookies.set('newsletter_formName', 'all', {
                      expires: new Date(new Date().getTime() + 60 * 60 * 1000),
                    });
                  }
                }}
              >
                Subscribe to all
              </button>
            )}
            <div className='carousel-content'>
              <div className='wrapper'>
                <S.Title data-cy={NEWSLETTERS_SECTION_TEST_IDS.NEWSLETTERS_SUBTITLE}>
                  Everything you need to be better at business — delivered to your inbox
                </S.Title>
                <S.Description data-cy={NEWSLETTERS_SECTION_TEST_IDS.NEWSLETTERS_DESCRIPTION}>
                  Fortune’s newsletters are packed with news, insights, and expert analysis on every facet of business.
                  Sign up today and get the best of Fortune’s journalism delivered straight to your inbox.
                </S.Description>
              </div>
              <NewsletterCarousel
                data-cy={NEWSLETTERS_SECTION_TEST_IDS.NEWSLETTERS_CAROUSEL}
                className='hero-carousel'
                newsletterHeroes={carouselNewsletters}
                onClick={(news: Newsletter) => () => {
                  if (news.subscriptionStatus !== 'subscribed') {
                    pushToDataLayer({
                      event: 'formInteract',
                      formName: `${news.title} Newsletter`,
                    });
                  }
                  if (email) {
                    setModalType(news.subscriptionStatus === 'subscribed' ? 'unsubscribe' : 'subscribe');
                    setModalTarget([news]);
                    if (news.subscriptionStatus !== 'subscribed') {
                      void handleSubscribe(news);
                    } else {
                      setIsModalOpen(true);
                    }
                  } else {
                    showLogin();
                    Cookies.set('newsletter_formName', news.formName, {
                      expires: new Date(new Date().getTime() + 60 * 60 * 1000),
                    });
                  }
                }}
              />
            </div>
          </S.CarouselHeroWrapper>
        </ErrorBoundary>
        <ErrorBoundary fragment={UIFragments.NEWSLETTER_PAGE}>
          <S.NewslettersWrapper
            id='newsletters'
            data-cy={NEWSLETTERS_SECTION_TEST_IDS.NEWSLETTERS}
          >
            {Object.entries(categorizedNewsletters).map(([title, news], i) => (
              <S.CategoryWrapper
                data-cy={NEWSLETTERS_SECTION_TEST_IDS.CATEGORY_WRAPPER}
                key={title}
                className={i % 2 === 0 ? 'dark' : ''}
              >
                <div>
                  <S.CategoryTitle data-cy={NEWSLETTERS_SECTION_TEST_IDS.CATEGORY_TITLE}>{title}</S.CategoryTitle>
                  <S.CategoryList data-cy={NEWSLETTERS_SECTION_TEST_IDS.CATEGORY_LIST}>
                    {news.map((newsInfo) => (
                      <NewsletterCard
                        data-cy={NEWSLETTERS_SECTION_TEST_IDS.NEWSLETTER_CARD}
                        key={newsInfo.formName}
                        title={newsInfo.title}
                        description={newsInfo.description}
                        sampleUrl={newsInfo.sampleUrl}
                        frequencyOfPublication={newsInfo.frequency}
                        subscriptionStatus={newsInfo.subscriptionStatus}
                        authors={newsInfo.authors}
                        {...(isGuest ? { className: 'guest' } : {})}
                        onClick={() => {
                          if (newsInfo.subscriptionStatus !== 'subscribed') {
                            pushToDataLayer({
                              event: 'formInteract',
                              formName: `${newsInfo.title} Newsletter`,
                            });
                          }
                          if (email) {
                            setModalType(newsInfo.subscriptionStatus === 'subscribed' ? 'unsubscribe' : 'subscribe');
                            setModalTarget([newsInfo]);
                            if (newsInfo.subscriptionStatus !== 'subscribed') {
                              void handleSubscribe(newsInfo);
                            } else {
                              setIsModalOpen(true);
                            }
                          } else {
                            showLogin();
                            Cookies.set('newsletter_formName', newsInfo.formName, {
                              expires: new Date(new Date().getTime() + 60 * 60 * 1000),
                            });
                          }
                        }}
                      />
                    ))}
                  </S.CategoryList>
                </div>
              </S.CategoryWrapper>
            ))}
          </S.NewslettersWrapper>
        </ErrorBoundary>
      </div>
    </S.Wrapper>
  );
};

export default NewslettersPage;
